'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Search, BookOpen, GraduationCap, Globe2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Stats {
  total: number;
  doctoral: number;
  master: number;
}

export default function HeroSearch() {
  const [query, setQuery] = useState('');
  const [stats, setStats] = useState<Stats>({ total: 0, doctoral: 0, master: 0 });

  const loadStats = useCallback(async () => {
    const supabase = createClient();
    try {
      const [totalResp, phdResp, masterResp] = await Promise.all([
        supabase.from('theses').select('id', { count: 'exact', head: true }).in('thesis_status', ['approved', 'published']),
        supabase.from('theses').select('id', { count: 'exact', head: true }).in('thesis_status', ['approved', 'published']).eq('doc_type', 'phd'),
        supabase.from('theses').select('id', { count: 'exact', head: true }).in('thesis_status', ['approved', 'published']).eq('doc_type', 'master'),
      ]);
      setStats({
        total: totalResp.count ?? 0,
        doctoral: phdResp.count ?? 0,
        master: masterResp.count ?? 0,
      });
    } catch (err) {
      console.log('HeroSearch stats error:', err);
    }
  }, []);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30_000);
    const handleFocus = () => loadStats();
    const handleVisibility = () => { if (!document.hidden) loadStats(); };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    const supabase = createClient();
    const channel = supabase
      .channel('public:theses:counts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'theses' }, () => loadStats())
      .subscribe();

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      supabase.removeChannel(channel);
    };
  }, [loadStats]);

  const statItems = [
    { icon: BookOpen, value: stats.total, label: 'Total Theses' },
    { icon: GraduationCap, value: stats.doctoral, label: 'Doctoral' },
    { icon: Globe2, value: stats.master, label: "Master's" },
  ];

  return (
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #111e3d 0%, #1a2e5a 50%, #243668 100%)' }}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 25% 50%, #c9a84c 0%, transparent 50%), radial-gradient(circle at 75% 20%, #ffffff 0%, transparent 40%)' }} />
      </div>
      <div className="relative max-w-screen-2xl mx-auto px-6 xl:px-10 2xl:px-16 py-16 md:py-20">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-0.5 w-8 bg-[#c9a84c]" />
            <span className="text-[#c9a84c] text-sm font-600 tracking-wide uppercase">Thesis Repository</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-800 text-white mb-3 leading-tight">
            Philips University<br /><span className="text-[#c9a84c]">Academic Repository</span>
          </h1>
          <p className="text-white/65 text-base mb-8 leading-relaxed">
            Search and browse master&apos;s and doctoral theses from Philips University Cyprus.
            Open access to academic knowledge — in Greek and English.
          </p>
          <div className="relative flex items-center bg-white rounded-xl shadow-2xl overflow-hidden">
            <Search size={18} className="absolute left-4 text-[#8892a8] pointer-events-none" />
            <input type="text" value={query} onChange={(e) => setQuery(e?.target?.value)}
              placeholder="Search by title, author, keywords, department..."
              className="flex-1 pl-11 pr-4 py-4 text-sm text-[#0f1a35] placeholder-[#8892a8] outline-none font-500" />
            <button className="m-1.5 bg-[#1a2e5a] hover:bg-[#111e3d] text-white font-600 px-6 py-2.5 rounded-lg text-sm transition-all duration-150 active:scale-95 flex-shrink-0">
              Search
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {['Master\'s Theses', 'PhD Dissertations', 'Computer Science', 'Business', 'Law', 'Open Access']?.map((tag) => (
              <button key={`quick-${tag}`} className="text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-3 py-1 rounded-full text-xs font-500 transition-all duration-150">
                {tag}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-8 mt-12 pt-8 border-t border-white/10">
          {statItems.map((s) => (
            <div key={`stat-${s.label}`} className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                <s.icon size={16} className="text-[#c9a84c]" />
              </div>
              <div>
                <div className="text-white font-700 text-lg tabular-nums leading-tight">{s.value}</div>
                <div className="text-white/50 text-xs">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
